import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/Dialog';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  getApprovalWorkflows, saveApprovalWorkflow, updateApprovalWorkflow,
  deleteApprovalWorkflow, getPolicyLeaveTypes
} from '../../../../../api/Attendance/leaves';
import { FetchUsers } from '../../../../../api/General/account';
import type {
  LeavePolicyView, WorkflowView, WorkflowInput,
  WorkflowStepView, WorkflowStepInput, LeaveTypeView
} from '../../../../../models/attendance';
import { ApproverSource, StepRoleType, TimeoutBehavior } from '../../../../../models/attendance';
import type { UserView } from '../../../../../models/general';

interface ApprovalWorkflowsTabProps {
  currentPolicy?: LeavePolicyView;
}

/** Parse an escalation target string into its source type and value */
const parseEscalationTarget = (target?: string): { type: ApproverSource; value: string } => {
  if (!target) return { type: ApproverSource.Manager, value: '' };
  if (target === 'Manager') return { type: ApproverSource.Manager, value: '' };
  if (target === 'HR') return { type: ApproverSource.HR, value: '' };
  if (target.startsWith('User:')) return { type: ApproverSource.SpecificUser, value: target.slice(5) };
  return { type: ApproverSource.Manager, value: '' };
};

/** Encode escalation source type + value into the escalation target string */
const encodeEscalationTarget = (type: ApproverSource, value: string): string => {
  if (type === ApproverSource.Manager) return 'Manager';
  if (type === ApproverSource.HR) return 'HR';
  if (type === ApproverSource.SpecificUser) return `User:${value}`;
  return value;
};

const ApprovalWorkflowsTab: React.FC<ApprovalWorkflowsTabProps> = ({ currentPolicy }) => {
  const { t } = useTranslation('leaves');
  const companyId = useSelector((state: any) => state.user?.company?.id);

  const [workflows, setWorkflows] = useState<WorkflowView[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [employees, setEmployees] = useState<UserView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<WorkflowView | null>(null);
  const [newWorkflow, setNewWorkflow] = useState<WorkflowInput | null>(null);

  const leaveTypeOptions = leaveTypes
    .filter(lt => lt.isActive)
    .map(lt => ({ value: lt.id, label: lt.name }));

  const approverSourceOptions = [
    { value: ApproverSource.Manager, label: t('Direct Manager') },
    { value: ApproverSource.HR, label: t('HR') },
    { value: ApproverSource.SpecificUser, label: t('Specific User') }
  ];

  const approverSourceLabels: Record<string, string> = {
    [ApproverSource.Manager]: t('Direct Manager'),
    [ApproverSource.HR]: t('HR'),
    [ApproverSource.SpecificUser]: t('Specific User')
  };

  const timeoutBehaviorOptions = [
    { value: TimeoutBehavior.Escalate, label: t('Escalate') },
    { value: TimeoutBehavior.AutoApprove, label: t('Auto-Approve') },
    { value: TimeoutBehavior.Skip, label: t('Skip') }
  ];

  const timeoutBehaviorLabels: Record<string, string> = {
    [TimeoutBehavior.Escalate]: t('Escalate'),
    [TimeoutBehavior.AutoApprove]: t('Auto-Approve'),
    [TimeoutBehavior.Skip]: t('Skip')
  };

  const roleTypeOptions = [
    { value: StepRoleType.Blocking, label: t('Blocking') },
    { value: StepRoleType.Advisory, label: t('Advisory') },
    { value: StepRoleType.FYI, label: t('FYI') }
  ];

  const roleTypeLabels: Record<string, string> = {
    [StepRoleType.Blocking]: t('Blocking'),
    [StepRoleType.Advisory]: t('Advisory'),
    [StepRoleType.FYI]: t('FYI')
  };

  const conditionOptions = [
    { value: 'leave_over_3_days', label: t('conditionLeaveOver3Days') },
    { value: 'leave_over_5_days', label: t('conditionLeaveOver5Days') },
    { value: 'consecutive_leave', label: t('conditionConsecutiveLeave') },
    { value: 'peak_season', label: t('conditionPeakSeason') },
    { value: 'probation_employee', label: t('conditionProbationEmployee') },
    { value: 'manager_level', label: t('conditionManagerLevel') }
  ];

  const employeeOptions = useMemo(() =>
    employees.map(emp => ({
      value: emp.id,
      label: emp.fullName || emp.userName,
      description: emp.title || emp.role
    })),
    [employees]
  );

  const loadWorkflows = useCallback(async () => {
    if (!currentPolicy?.id) return;
    setIsLoading(true);
    try {
      const result = await getApprovalWorkflows(currentPolicy.id);
      if (!result?.errors?.length) {
        setWorkflows(result.payload ?? []);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPolicy?.id]);

  const loadLeaveTypes = useCallback(async () => {
    if (!currentPolicy?.id) return;
    try {
      const result = await getPolicyLeaveTypes(currentPolicy.id);
      if (!result?.errors?.length) {
        setLeaveTypes(result.payload ?? []);
      }
    } catch {
      // Leave types unavailable - leave dropdown empty
    }
  }, [currentPolicy?.id]);

  const loadEmployees = useCallback(async () => {
    if (!companyId) return;
    try {
      const result = await FetchUsers('Company', companyId);
      if (result.errors.length === 0) {
        setEmployees(result.payload ?? []);
      }
    } catch {
      // Employees unavailable
    }
  }, [companyId]);

  useEffect(() => {
    if (currentPolicy?.id) {
      loadWorkflows();
      loadLeaveTypes();
    }
  }, [currentPolicy?.id, loadWorkflows, loadLeaveTypes]);

  useEffect(() => {
    if (companyId) loadEmployees();
  }, [companyId, loadEmployees]);

  /** Parse LeaveTypes JSON string "[1,2,3]" to number array */
  const parseLeaveTypeIds = (leaveTypesJson?: string): number[] => {
    if (!leaveTypesJson) return [];
    try {
      const parsed = JSON.parse(leaveTypesJson);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(n => !isNaN(n));
    } catch {
      // fallback: empty
    }
    return [];
  };

  /** Resolve leave type IDs to names for display */
  const getLeaveTypeNames = (leaveTypesJson?: string): string[] => {
    const ids = parseLeaveTypeIds(leaveTypesJson);
    return ids.map(id => {
      const lt = leaveTypes.find(l => l.id === id);
      return lt?.name ?? `#${id}`;
    });
  };

  /** Resolve condition keys to display labels */
  const getConditionLabels = (conditionsStr?: string): string[] => {
    if (!conditionsStr) return [];
    const keys = conditionsStr.split(',').map(s => s.trim()).filter(Boolean);
    const labelMap: Record<string, string> = {};
    conditionOptions.forEach(opt => { labelMap[opt.value] = opt.label; });
    return keys.map(k => labelMap[k] ?? k);
  };

  /** Resolve employee ID to name */
  const getEmployeeName = (userId?: string): string => {
    if (!userId) return '';
    const emp = employees.find(e => e.id === userId);
    return emp ? (emp.fullName || emp.userName) : userId;
  };

  const viewToInput = (wf: WorkflowView): WorkflowInput => ({
    name: wf.name,
    leaveTypes: wf.leaveTypes,
    conditions: wf.conditions,
    conditionLogic: wf.conditionLogic,
    priority: wf.priority,
    isDefault: wf.isDefault,
    autoApprovalEnabled: wf.autoApprovalEnabled,
    autoApprovalMaxDays: wf.autoApprovalMaxDays,
    steps: wf.steps.map(stepViewToInput)
  });

  const stepViewToInput = (step: WorkflowStepView): WorkflowStepInput => ({
    stepOrder: step.stepOrder,
    approverSource: step.approverSource,
    approverValue: step.approverValue,
    roleType: step.roleType,
    timeLimitDays: step.timeLimitDays,
    timeoutBehavior: step.timeoutBehavior,
    escalationTarget: step.escalationTarget
  });

  const handleEditWorkflow = (workflow: WorkflowView): void => {
    setEditingWorkflow(workflow);
    setNewWorkflow(viewToInput(workflow));
    setShowModal(true);
  };

  const handleDeleteWorkflow = async (id: number): Promise<void> => {
    if (!currentPolicy?.id) return;
    if (!window.confirm(t('Are you sure you want to delete this item?'))) return;
    try {
      const result = await deleteApprovalWorkflow(currentPolicy.id, id);
      if (!result?.errors?.length) {
        await loadWorkflows();
      }
    } catch {
      // silently handle
    }
  };

  const handleSaveWorkflow = async (): Promise<void> => {
    if (!newWorkflow || !currentPolicy?.id) return;
    setIsSaving(true);
    try {
      if (editingWorkflow) {
        const result = await updateApprovalWorkflow(editingWorkflow.id, newWorkflow);
        if (!result?.errors?.length) {
          await loadWorkflows();
          resetForm();
        }
      } else {
        const result = await saveApprovalWorkflow(currentPolicy.id, newWorkflow);
        if (!result?.errors?.length) {
          await loadWorkflows();
          resetForm();
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const initializeNewWorkflow = (): void => {
    setNewWorkflow({
      name: '',
      leaveTypes: '',
      conditions: '',
      conditionLogic: '',
      priority: workflows.length + 1,
      isDefault: false,
      autoApprovalEnabled: false,
      autoApprovalMaxDays: 0,
      steps: [{
        stepOrder: 1,
        approverSource: ApproverSource.Manager,
        roleType: StepRoleType.Blocking,
        timeLimitDays: 2,
        timeoutBehavior: TimeoutBehavior.Escalate,
        escalationTarget: ''
      }]
    });
    setEditingWorkflow(null);
    setShowModal(true);
  };

  const resetForm = (): void => {
    setShowModal(false);
    setEditingWorkflow(null);
    setNewWorkflow(null);
  };

  const addStep = (): void => {
    if (!newWorkflow) return;
    const stepOrder = newWorkflow.steps.length + 1;
    setNewWorkflow({
      ...newWorkflow,
      steps: [...newWorkflow.steps, {
        stepOrder,
        approverSource: ApproverSource.HR,
        roleType: StepRoleType.Blocking,
        timeLimitDays: 1,
        timeoutBehavior: TimeoutBehavior.AutoApprove,
        escalationTarget: ''
      }]
    });
  };

  const removeStep = (index: number): void => {
    if (!newWorkflow || newWorkflow.steps.length <= 1) return;
    const steps = newWorkflow.steps
      .filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, stepOrder: i + 1 }));
    setNewWorkflow({ ...newWorkflow, steps });
  };

  const updateStep = (index: number, updates: Partial<WorkflowStepInput>): void => {
    if (!newWorkflow) return;
    const steps = [...newWorkflow.steps];
    steps[index] = { ...steps[index], ...updates };
    setNewWorkflow({ ...newWorkflow, steps });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="animate-spin text-primary" />
        <span className="ms-2 text-sm text-muted-foreground">{t('Loading workflows...')}</span>
      </div>
    );
  }

  if (!currentPolicy) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Icon name="AlertCircle" size={32} className="mx-auto mb-2 text-muted-foreground/60" />
        <p className="text-sm">{t('Please select a leave policy first')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('Approval Workflows')}</h3>
          <p className="text-sm text-muted-foreground">{t('Configure multi-step approval workflows with conditions and escalation rules')}</p>
        </div>
        <Button variant="default" iconName="Plus" onClick={initializeNewWorkflow} data-testid="add-workflow-btn">
          {t('Add Workflow')}
        </Button>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('Approval Workflows Configuration')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('Build multi-step approval workflows with configurable time limits, escalation paths, and auto-approval rules.')}
            </p>
          </div>
        </div>
      </div>

      {currentPolicy && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{t('Current Policy')}: {currentPolicy.name}</span>
          </div>
        </div>
      )}

      {/* Workflow Cards */}
      {workflows.length === 0 ? (
        <div className="text-center py-8 bg-muted/50 border border-border rounded-lg">
          <Icon name="GitBranch" size={32} className="mx-auto mb-2 text-muted-foreground/60" />
          <p className="text-sm text-muted-foreground">{t('No approval workflows configured yet')}</p>
          <p className="text-xs text-muted-foreground/80 mt-1">{t('Add a workflow to define approval steps for leave requests')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {workflows.map((workflow) => (
            <div key={workflow.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow" data-testid="workflow-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <h4 className="text-md font-semibold text-foreground">{workflow.name}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${workflow.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                      {workflow.isActive ? t('Active') : t('Inactive')}
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">{t('Priority')}: {workflow.priority}</span>
                    {workflow.isDefault && (
                      <span className="px-2 py-0.5 text-xs bg-warning/10 text-warning rounded-full">{t('Default')}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {getLeaveTypeNames(workflow.leaveTypes).map((name, i) => (
                      <span key={i} className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded">{name}</span>
                    ))}
                    {getConditionLabels(workflow.conditions).map((label, i) => (
                      <span key={`cond-${i}`} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">{label}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <Button variant="ghost" size="sm" iconName="Edit" onClick={() => handleEditWorkflow(workflow)} data-testid={`edit-workflow-${workflow.id}`} />
                  <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => handleDeleteWorkflow(workflow.id)} className="text-destructive" data-testid={`delete-workflow-${workflow.id}`} />
                </div>
              </div>

              {/* Workflow Steps Visual */}
              <div className="flex items-center space-x-2 rtl:space-x-reverse overflow-x-auto pb-2">
                {workflow.steps.map((step, i) => (
                  <React.Fragment key={i}>
                    <div className="flex-shrink-0 p-3 bg-primary/5 border border-primary/20 rounded-lg min-w-[180px]">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse mb-1">
                        <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">{step.stepOrder}</div>
                        <span className="text-sm font-medium text-foreground">{approverSourceLabels[step.approverSource] ?? step.approverSource}</span>
                      </div>
                      {step.approverSource === ApproverSource.SpecificUser && step.approverValue && (
                        <div className="text-xs text-primary font-medium mb-1">{getEmployeeName(step.approverValue)}</div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        <div>{t('Role')}: {roleTypeLabels[step.roleType] ?? step.roleType}</div>
                        <div>{t('Time limit')}: {step.timeLimitDays} {t('days')}</div>
                        <div>{t('Timeout')}: {timeoutBehaviorLabels[step.timeoutBehavior] ?? step.timeoutBehavior}</div>
                      </div>
                    </div>
                    {i < workflow.steps.length - 1 && (
                      <Icon name="ArrowRight" size={20} className="text-muted-foreground/60 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>

            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal && !!newWorkflow} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>
              {editingWorkflow ? t('Edit Workflow') : t('Add Workflow')}
            </DialogTitle>
          </DialogHeader>

          {newWorkflow && (
            <>
              <DialogBody className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Workflow Name')} *</label>
                    <Input
                      type="text"
                      value={newWorkflow.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
                      placeholder={t('e.g., Standard Approval')}
                      data-testid="workflow-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Priority')}</label>
                    <Input
                      type="number"
                      value={newWorkflow.priority}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewWorkflow({ ...newWorkflow, priority: parseInt(e.target.value) || 1 })}
                      min="1"
                      data-testid="workflow-priority-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Leave Types')}</label>
                    <Select
                      value={parseLeaveTypeIds(newWorkflow.leaveTypes)}
                      onChange={(value: number[]) => setNewWorkflow({ ...newWorkflow, leaveTypes: JSON.stringify(value) })}
                      options={leaveTypeOptions}
                      multiple
                      placeholder={t('Select leave types')}
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('Leave empty to apply to all leave types')}</p>
                  </div>
                </div>

                {/* Default & Active toggles */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Checkbox
                      checked={newWorkflow.isDefault}
                      onCheckedChange={(checked: boolean) => setNewWorkflow({ ...newWorkflow, isDefault: checked })}
                      data-testid="workflow-default-checkbox"
                    />
                    <span className="text-sm text-muted-foreground">{t('Default workflow')}</span>
                  </label>
                </div>

                {/* Conditions */}
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Conditions')}</label>
                  <Select
                    value={(newWorkflow.conditions ?? '').split(',').map(s => s.trim()).filter(Boolean)}
                    onChange={(value: string[]) => setNewWorkflow({ ...newWorkflow, conditions: value.join(',') })}
                    options={conditionOptions}
                    multiple
                    placeholder={t('Select conditions')}
                    data-testid="workflow-conditions-select"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('conditionsHelpText')}</p>
                </div>

                {/* Workflow Steps */}
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-foreground">{t('Approval Steps')}</h4>
                    <Button variant="outline" size="sm" iconName="Plus" onClick={addStep} data-testid="add-step-btn">
                      {t('Add Step')}
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {newWorkflow.steps.map((step, index) => (
                      <div key={index} className="p-4 bg-card rounded-lg border border-border" data-testid={`workflow-step-${index}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2 rtl:space-x-reverse">
                            <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">{step.stepOrder}</div>
                            <span className="text-sm font-medium text-foreground">{t('Step')} {step.stepOrder}</span>
                          </div>
                          {newWorkflow.steps.length > 1 && (
                            <Button variant="ghost" size="sm" iconName="X" onClick={() => removeStep(index)} className="text-destructive" data-testid={`remove-step-${index}`} />
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Approver Source')}</label>
                            <Select
                              value={step.approverSource}
                              onChange={(value: string) => updateStep(index, { approverSource: value as ApproverSource })}
                              options={approverSourceOptions}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Role Type')}</label>
                            <Select
                              value={step.roleType}
                              onChange={(value: string) => updateStep(index, { roleType: value as StepRoleType })}
                              options={roleTypeOptions}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Time Limit (days)')}</label>
                            <Input
                              type="number"
                              value={step.timeLimitDays}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateStep(index, { timeLimitDays: parseInt(e.target.value) || 1 })}
                              min="1"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Timeout Behavior')}</label>
                            <Select
                              value={step.timeoutBehavior}
                              onChange={(value: string) => {
                                const updates: Partial<WorkflowStepInput> = { timeoutBehavior: value as TimeoutBehavior };
                                if (value !== TimeoutBehavior.Escalate) {
                                  updates.escalationTarget = '';
                                }
                                updateStep(index, updates);
                              }}
                              options={timeoutBehaviorOptions}
                            />
                          </div>
                        </div>

                        {/* Approver Value */}
                        {step.approverSource === ApproverSource.SpecificUser && (
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-muted-foreground mb-1">
                              {t('Approver')}
                            </label>
                            <Select
                              value={step.approverValue ?? ''}
                              onChange={(value: string) => updateStep(index, { approverValue: value })}
                              options={employeeOptions}
                              searchable
                              placeholder={t('Search and select employee')}
                              data-testid={`step-approver-user-${index}`}
                            />
                          </div>
                        )}

                        {/* Escalation Target */}
                        {step.timeoutBehavior === TimeoutBehavior.Escalate && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Escalation Type')}</label>
                              <Select
                                value={parseEscalationTarget(step.escalationTarget).type}
                                onChange={(value: string) => {
                                  const encoded = encodeEscalationTarget(value as ApproverSource, '');
                                  updateStep(index, { escalationTarget: encoded });
                                }}
                                options={approverSourceOptions}
                                data-testid={`step-escalation-type-${index}`}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Escalation Target')}</label>
                              {(() => {
                                const parsed = parseEscalationTarget(step.escalationTarget);
                                if (parsed.type === ApproverSource.SpecificUser) {
                                  return (
                                    <Select
                                      value={parsed.value}
                                      onChange={(value: string) => updateStep(index, { escalationTarget: encodeEscalationTarget(ApproverSource.SpecificUser, value) })}
                                      options={employeeOptions}
                                      searchable
                                      placeholder={t('Search and select employee')}
                                      data-testid={`step-escalation-user-${index}`}
                                    />
                                  );
                                }
                                return (
                                  <div className="flex items-center gap-2 h-10 px-3 bg-muted/50 border border-border rounded-md">
                                    <Icon name="Users" size={14} className="text-muted-foreground/60" />
                                    <span className="text-sm text-muted-foreground">
                                      {parsed.type === ApproverSource.HR
                                        ? t('Automatically resolved to HR personnel')
                                        : t('Automatically resolved from employee org chart')}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

              </DialogBody>

              <DialogFooter>
                <Button variant="outline" onClick={resetForm} data-testid="leave-workflow-modal-cancel">{t('Cancel')}</Button>
                <Button variant="default" onClick={handleSaveWorkflow} disabled={!newWorkflow.name || isSaving} data-testid="workflow-save-btn">
                  {isSaving ? t('Saving...') : editingWorkflow ? t('Update Workflow') : t('Add Workflow')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalWorkflowsTab;
